import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  completeDoorStep,
  type FilterLevel,
  type NotifyLevel,
  type StarterVocabulary,
} from "../../lib/api";
import { API_URL } from "../../lib/config";
import { qk } from "../../lib/queries";
import { track } from "../../lib/telemetry";
import { OnboardShell, StepDots, WaitingAtDoor } from "./steps";

/**
 * STEP 4 OF THE RUN, RULES (PRD §2.2.5, FR-2.2, changelog #7; rulings C5, R-0826-3).
 *
 * THREE QUESTIONS, ALL ANSWERED BY TAPPING, and every one of them optional —
 * the defaults compose a working door (FR-1.2 #5). What the answers become is
 * PROSE: each dial position writes a paragraph into `nob.md` that the owner can
 * read and rewrite, and nothing anywhere stores a level as a level. That is the
 * v3.2 objection answered rather than overruled — what was refused was a number
 * kept as a number, not a screen (§2.2.5's own note).
 *
 * THE SENTENCES ARE THE SERVER'S. The chips and the two preview cards render
 * text that arrived with the run's state, because that text is what gets
 * written into the document; a copy of it in this bundle would be a second
 * copy of a rule, drifting from the first the day either is edited.
 *
 * THE DOOR IS ALREADY LIVE (ruling R-1006-2). The server hung it with the
 * starter rules when the email step finished, so this screen TUNES a door that
 * exists: the answers are written as a new rules version. There is no further
 * "your door is live" confirmation — v3.2 had one and it is deleted as a step
 * that provides no value.
 *
 * NO AI WORK RUNS HERE (ruling C10). Free performs none, and this step can be
 * reached on Free; the file is composed deterministically from the answers, and
 * the conversational interview that CAN redraft it lives afterwards, in the
 * desk thread, behind FR-11's third prompt.
 */

export const RULES_TITLE = "Your House Rules";
export const RULES_LEAD =
  "These are the instructions your Nob follows to know what to ask and who to let in. " +
  "You can always change them manually, or ask Nob to do it for you.";
export const INTERESTS_QUESTION = "What are you interested in?";
export const INTERESTS_CARD = "Priorities & interests";
export const INTERESTS_HINT = "You want to hear from";
export const FILTER_CARD = "Filtering level";
export const FILTER_HINT = "Difficulty level to pass your Nob";
export const STRICTER_LABEL = "Make it stricter";
export const LOOSER_LABEL = "Make it less strict";
export const NOTIFY_CARD = "Notifications";
export const NOTIFY_HINT = "What you see from your Nob";
export const QUIETER_LABEL = "Get less";
export const NOISIER_LABEL = "Get more";
export const HANG_LABEL = "Save these rules";
export const HANGING_LABEL = "Saving the rules…";
export const RULES_UNREACHABLE = "Nob is not answering. Try that again.";

const FILTER_ORDER: FilterLevel[] = ["easy", "medium", "hard"];
const NOTIFY_ORDER: NotifyLevel[] = ["less", "medium", "more"];

/** One step at a time, and both ends are terminal. */
function step<T>(order: T[], current: T, by: 1 | -1): T {
  const at = order.indexOf(current);
  return order[Math.min(order.length - 1, Math.max(0, at + by))] ?? current;
}

type Props = {
  starter: StarterVocabulary;
  /**
   * People already waiting at this handle (owner item 3). Said again HERE, on
   * the step that tunes the door, because this is the screen somebody
   * abandons: three questions is the most work the run asks for, and the reason
   * to answer them is standing outside.
   */
  waitingCount: number;
  /** The owner's handle: the door is already live at it (ruling R-1006-2). */
  handle?: string | null;
  onHung: () => void;
  /**
   * The way past the step without a door (owner, 2026-08-27). Small and at the
   * bottom, because the door is the product and this screen should still argue
   * for it — but a person who wants the console first is let through, and the
   * pinned Nob row is always the way back to this step.
   */
  onSkip: () => void;
  /**
   * This run was a knocker's, and the counter has to keep faith with it: they
   * were walked through three steps and told so on each one. Coming back for
   * the step they deferred does not make the run four steps long — see
   * `StepDots`, which reads this and clamps.
   */
  knocker: boolean;
};

export const SKIP_DOOR_LABEL = "Skip for now — tune your door later";

/**
 * The counter here is ALWAYS THE LAST STEP OF THE RUN, named "Rules": four of
 * four on a door owner's run, three of three for a knocker coming back for the
 * step they deferred. It is the last either way — tuning the door is the end of
 * the path, and anybody on this screen is finishing whichever one they were
 * counted through.
 */
export function OnboardingDoorStep({
  starter,
  waitingCount,
  handle = null,
  onHung,
  onSkip,
  knocker,
}: Props) {
  const queryClient = useQueryClient();
  const [interests, setInterests] = useState<string[]>([]);
  const [filterLevel, setFilterLevel] = useState<FilterLevel>("medium");
  const [notifyLevel, setNotifyLevel] = useState<NotifyLevel>("medium");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) =>
    setInterests((on) => (on.includes(id) ? on.filter((x) => x !== id) : [...on, id]));

  /**
   * Listed in the order the chips are, not the order they were tapped, so the
   * card reads the same for two people who chose the same things — and the same
   * way round as the document the server will compose.
   */
  const chosen = starter.interests.filter((i) => interests.includes(i.id));

  const hang = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const r = await completeDoorStep({
      interests,
      filter_level: filterLevel,
      notify_level: notifyLevel,
    });
    if (!r.ok) {
      setBusy(false);
      setError(RULES_UNREACHABLE);
      return;
    }
    track({ name: "onboarding_rules_done" });
    // Everything about a door has just changed: the run, the settings the shell
    // reads, and the thread the console opens on.
    await queryClient.invalidateQueries({ queryKey: qk.onboarding() });
    await queryClient.invalidateQueries({ queryKey: qk.doorSettings() });
    await queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
    setBusy(false);
    onHung();
  }, [busy, filterLevel, interests, notifyLevel, onHung, queryClient]);

  return (
    <OnboardShell>
      <StepDots step={4} knocker={knocker} label="Rules" />
      <h1 className="nob-poster-title">{RULES_TITLE}</h1>
      {handle && (
        <p className="nob-poster-lead" data-door-live>
          Your door is live at{" "}
          <a href={`${API_URL}/@${handle}`} target="_blank" rel="noreferrer" className="underline">
            nob.me/@{handle}
          </a>
          .
        </p>
      )}
      <p className="nob-poster-lead">{RULES_LEAD}</p>
      <WaitingAtDoor count={waitingCount} />

      {/*
        The three questions are the one part of a poster that has to be worked
        rather than read, so they get their own scrolling well inside the
        column: left-aligned, because a list of sentences set centred is a list
        nobody reads, and capped in height so the button that ends the run stays
        on the sheet.
      */}
      <div className="nob-rules-well">
        <p className="nob-rules-q">{INTERESTS_QUESTION}</p>
        <div className="nob-rules-chips">
          {starter.interests.map((interest) => {
            const on = interests.includes(interest.id);
            return (
              <button
                key={interest.id}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(interest.id)}
                data-on={on ? "true" : "false"}
                className="nob-rules-chip"
              >
                <span aria-hidden>{on ? "\u2212" : "+"}</span>
                {interest.label}
              </button>
            );
          })}
        </div>

        <RuleCard title={INTERESTS_CARD} hint={INTERESTS_HINT}>
          {chosen.length > 0 ? (
            <ul className="nob-rules-list">
              {chosen.map((i) => (
                <li key={i.id}>{i.sentence}</li>
              ))}
            </ul>
          ) : (
            <p className="nob-rules-body">{starter.no_interests}</p>
          )}
        </RuleCard>

        <RuleCard title={FILTER_CARD} hint={FILTER_HINT}>
          <p className="nob-rules-body">{starter.filter_text[filterLevel] ?? ""}</p>
          {/* Both ends are terminal, so the button that would step past one is
              the button that goes quiet. */}
          <Dial
            up={STRICTER_LABEL}
            down={LOOSER_LABEL}
            upDisabled={filterLevel === "hard"}
            downDisabled={filterLevel === "easy"}
            onUp={() => setFilterLevel(step(FILTER_ORDER, filterLevel, 1))}
            onDown={() => setFilterLevel(step(FILTER_ORDER, filterLevel, -1))}
          />
        </RuleCard>

        <RuleCard title={NOTIFY_CARD} hint={NOTIFY_HINT}>
          <p className="nob-rules-body">{starter.notify_text[notifyLevel] ?? ""}</p>
          <Dial
            up={NOISIER_LABEL}
            down={QUIETER_LABEL}
            upDisabled={notifyLevel === "more"}
            downDisabled={notifyLevel === "less"}
            onUp={() => setNotifyLevel(step(NOTIFY_ORDER, notifyLevel, 1))}
            onDown={() => setNotifyLevel(step(NOTIFY_ORDER, notifyLevel, -1))}
          />
        </RuleCard>
      </div>

      <div className="nob-poster-cta">
        <button
          type="button"
          disabled={busy}
          onClick={() => void hang()}
          className="nob-poster-btn"
        >
          {busy ? HANGING_LABEL : HANG_LABEL}
        </button>
      </div>
      {error && (
        <p className="nob-poster-error" role="alert">
          {error}
        </p>
      )}
      <p className="mt-6 text-center">
        <button
          type="button"
          onClick={onSkip}
          className="nob-poster-inline text-[13px]"
          disabled={busy}
        >
          {SKIP_DOOR_LABEL}
        </button>
      </p>
    </OnboardShell>
  );
}

function RuleCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="nob-rules-card">
      <p className="nob-rules-title">{title}</p>
      <p className="nob-rules-hint">{hint}</p>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Dial({
  up,
  down,
  upDisabled,
  downDisabled,
  onUp,
  onDown,
}: {
  up: string;
  down: string;
  upDisabled: boolean;
  downDisabled: boolean;
  onUp: () => void;
  onDown: () => void;
}) {
  return (
    <div className="nob-rules-dial">
      <button type="button" className="nob-rules-step" disabled={upDisabled} onClick={onUp}>
        {up}
      </button>
      <button type="button" className="nob-rules-step" disabled={downDisabled} onClick={onDown}>
        {down}
      </button>
    </div>
  );
}
